<template>
  <div class="container-fluid container-top mt-5">
    <div class="text-head font-weight-bold h2 py-0 my-0 text-black">
      Social & Mobile App
    </div>

    <div class="container py-5" style="max-width: 600px">
      <!-- BUSINESS ACCOUNT SELECT -->
      <div class="form-group mb-3">
        <label class="fw-500 s-20 mb-2" for="account-select"
          >Select Account</label
        >
        <el-select
          v-model="selectedAccount"
          id="account-select"
          placeholder="Select Account"
          class="w-100 custo"
          size="large"
          :loading="isLoadingAccounts"
          :disabled="isLoadingAccounts"
        >
          <el-option
            v-for="account in accounts"
            :key="account.id"
            :label="account.name"
            :value="account.id"
          />
        </el-select>
      </div>

      <!-- PAGE SELECT -->
      <div class="form-group mb-3">
        <label class="fw-500 s-20 mb-2" for="page-select">Select Page</label>
        <el-select
          v-model="selectedPageId"
          id="page-select"
          placeholder="Select Page"
          class="w-100 custom-sect"
          size="large"
          :loading="isLoadingPages"
          :disabled="isLoadingPages || !selectedAccount"
        >
          <el-option
            v-for="page in pages"
            :key="page.id"
            :label="page.name"
            :value="page.id"
          >
            <div class="d-flex align-items-center">
              <img
                :src="page.image"
                :alt="page.name"
                class="rounded-circle mr-2"
                style="width: 28px; height: 28px"
              />
              <span>{{ page.name }}</span>
            </div>
          </el-option>
        </el-select>
      </div>

      <!-- INSTAGRAM ACCOUNT DISPLAY -->
      <div v-if="selectedPage" class="form-group mb-3">
        <label class="fw-500 s-20 mb-2">Instagram Account</label>
        <div class="instagram-input d-flex align-items-center">
          <img
            :src="selectedPage.image"
            :alt="selectedPage.name"
            class="rounded-circle mr-2"
            style="width: 28px; height: 28px"
          />
          <div>
            {{ selectedPage.name }}
          </div>
        </div>
      </div>

      <!-- CONNECT BUTTON -->
      <div class="text-center mt-5">
        <el-button
          :color="primarycolor"
          class="text-white py-4 w-100"
          size="large"
          @click="connect"
          :loading="isConnecting"
          :disabled="!selectedAccount || !selectedPageId"
          round
        >
          Connect
        </el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import axios from "@/gateway/backendapi";
import { ElNotification } from "element-plus";

const primarycolor = inject("primarycolor");
const router = useRouter();

// Reactive state
const accounts = ref([]);
const pages = ref([]);
const selectedAccount = ref(null);
const selectedPageId = ref(null);
const isLoadingAccounts = ref(true);
const isLoadingPages = ref(false);
const isConnecting = ref(false);

const selectedPage = computed(() =>
  pages.value.find((page) => page.id === selectedPageId.value)
);

// ✅ Fetch businesses on mount
onMounted(async () => {
  try {
    const { data } = await axios.get("/api/MetaBusiness/businesses");
    accounts.value = data;

    if (accounts.value.length > 0) {
      selectedAccount.value = accounts.value[0].id;
      await fetchPages(selectedAccount.value);
    }
  } catch (err) {
    ElNotification({
      type: "error",
      message: "Error fetching businesses" || err,
      duration: 5000,
    });
  } finally {
    isLoadingAccounts.value = false;
  }
});

// ✅ Watch selected account and fetch pages dynamically
watch(selectedAccount, async (newAccountId) => {
  if (newAccountId) await fetchPages(newAccountId);
});

const fetchPages = async (businessId) => {
  isLoadingPages.value = true;
  try {
    const { data } = await axios.get(
      `/api/MetaBusiness/businesses/${businessId}/pages`
    );
    pages.value = data;

    if (pages.value.length > 0) {
      selectedPageId.value = pages.value[0].id;
    } else {
      selectedPageId.value = null;
    }
  } catch (err) {
    ElNotification({
      type: "error",
      message: "Error fetching businesses" || err,
      duration: 5000,
    });
  } finally {
    isLoadingPages.value = false;
  }
};

// ✅ Connect to Instagram account for selected page
const connect = async () => {
  if (!selectedPageId.value) return;
  ElNotification({
    type: "error",
    message: "Please select a page first.",
    duration: 5000,
  });

  isConnecting.value = true;
  try {
    const selected = pages.value.find((p) => p.id === selectedPageId.value);

    const { data } = await axios.get(
      `/api/MetaBusiness/pages/${selected.id}/instagram-account?pageAccessToken=${selected.accessToken}`
    );
    const igAccount = data;

    if (!igAccount || !igAccount.id) {
      ElNotification({
        type: "error",
        message: "No Instagram account linked to this page!",
        duration: 5000,
      });

      return;
    }

    let payload = {
      mediaUrl: selectedAccount.value,
      caption: selected.id,
      mediaType: igAccount.id,
    };

    // Optionally persist connection on backend
    await axios.post(`/api/MetaBusiness/instagram/posts`, payload);

    // Redirect to feed after connection
    router.push("/tenant/social/feed/facebook");
  } catch (err) {
    ElNotification({
      type: "error",
      message: "Error connecting to Instagram:" || err,
      duration: 5000,
    });
  } finally {
    isConnecting.value = false;
  }
};
</script>

<style scoped>
.form-group label {
  font-size: 0.9rem;
}

.custom-select .el-input__inner {
  border-radius: 8px !important;
  background-color: #f7f7f7;
  border-color: #f7f7f7;
}

.el-select .el-input .el-input__wrapper {
  border-radius: 8px !important;
  box-shadow: none !important;
}

.instagram-input {
  border-radius: 8px !important;
  border: 1px solid #787878;
  padding: 10px;
}

.el-input.is-disabled .el-input__inner {
  background-color: #f7f7f7 !important;
}
</style>
